import { AfterViewInit, ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { PAGE } from '../app.routes';
import { dateFormat, DocumentItemTypeEnum, LegalDocument, LegalDocumentAcceptance, LegalDocumentTypeEnum, NoMandatoryConsensus } from '../core';
import { UserRequestService } from '../services';
import { BaseComponent, ConvertDatePipe } from '../shared';


/**
* Page for  legal documents acceptance.
*/
@Component({
	selector: 'app-legal-terms',
	templateUrl: './legal-terms.component.html',
	imports: [
		ConvertDatePipe
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class LegalTermsComponent extends BaseComponent implements OnInit, AfterViewInit {

	private readonly router = inject(Router);
	private readonly service = inject(UserRequestService);

	@Input() isAcceptance = false;
	@Input() sendLbl: string = this.labels().LEGAL_TERMS_COMPONENT_BUTTON_SEND;

	mySwiper: any;
	isNextDisabled = true;
	nextOrSendButton = '';
	yesItem = '';
	noItem = '';

	readonly dateFormat = dateFormat();
	private _legalDocuments: LegalDocument[] = [];

	EULAForm: EULAForm | null = null;
	PPForm: PPForm | null = null;
	DPAForm: DPAForm | null = null;

	ngOnInit(): void {
		this._legalDocuments = this.service.getLegalDocumentToAccept() ?? [];
		if (this._legalDocuments?.length) {
			this.initEULAForm();
			this.initPPForm();
			this.initDPAForm();
		} else {
			console.error('No legal documents to accept');
			this.router.navigateByUrl(PAGE.ERROR);
		}
		this.yesItem = this.service.getYesItem();
		this.noItem = this.service.getNoItem();
		this.nextOrSendButton = 'Next';
	}

	ngAfterViewInit() {
		setTimeout(() => {
			this.mySwiper = new Swiper('.swiper-container', {
				modules: [Navigation],
				allowTouchMove: false,
				autoHeight: true,
				spaceBetween: 50,
				pagination: {
					el: '.swiper-pagination',
					type: 'fraction',
				},
			});
		});
	}

	/**
	* Init form for UELA document.
	*/
	initEULAForm(): void {
		const eula = this._legalDocuments.find(doc => doc.type === LegalDocumentTypeEnum.EULA);
		if (eula) {
			this.EULAForm = new EULAForm();
			this.EULAForm.text = eula.text;
			this.EULAForm.effectiveDate = eula.effectiveDate ?? null;
			this.EULAForm.eula1 = eula.items.find(item => item.itemType === DocumentItemTypeEnum.EULA_I_Accept)?.displayItem ?? null;
			this.EULAForm.isAccepted = false;
			this.EULAForm.add_msg = eula.items.find(item => item.itemType === DocumentItemTypeEnum.EULA_Additional_Msg)?.displayItem ?? null;
		} else {
			this.EULAForm = null;
		}
	}

	/**
	* Init form for Privacy Policy document.
	*/
	initPPForm(): void {
		const pp = this._legalDocuments.find(doc => doc.type === LegalDocumentTypeEnum.PrivacyPolicy);
		if (pp) {
			this.PPForm = new PPForm();
			this.PPForm.text = pp.text;
			this.PPForm.effectiveDate = pp.effectiveDate ?? null;
			this.PPForm.ack = pp.items.find(item => item.itemType === DocumentItemTypeEnum.PP_Acknowledgement)?.displayItem ?? null;
			this.PPForm.isAccepted = false;
			const marketing = pp.items.find(item => item.itemType === DocumentItemTypeEnum.PP_DirectMarketing);
			this.PPForm.marketingId = marketing?.id ?? null;
			this.PPForm.marketingTxt = marketing?.displayItem ?? null;
			this.PPForm.marketingValue = null;
			const profilation = pp.items.find(item => item.itemType === DocumentItemTypeEnum.PP_UserProfilation);
			this.PPForm.profilationId = profilation?.id ?? null;
			this.PPForm.profilationTxt = profilation?.displayItem ?? null;
			this.PPForm.profilationValue = null;
		} else {
			this.PPForm = null;
		}
	}

	/**
	* Init form for Data Process Agreement document.
	*/
	initDPAForm(): void {
		const dpa = this._legalDocuments.find(doc => doc.type === LegalDocumentTypeEnum.DPA);
		if (dpa) {
			this.DPAForm = new DPAForm();
			this.DPAForm.text = dpa.text;
			this.DPAForm.effectiveDate = dpa.effectiveDate ?? null;
			this.DPAForm.ack = dpa.items.find(item => item.itemType === DocumentItemTypeEnum.DPA_Acknowledgement)?.displayItem ?? null;
			this.DPAForm.isAccepted = false;
		} else {
			this.DPAForm = null;
		}
	}

	/**
	* Handle EULA document acceptance.
	*/
	acceptEULA(value: boolean): void {
		this.EULAForm!.isAccepted = value;
		this.verifyEULA();
	}

	/**
	* Verify if EULA document is accepted.
	*/
	verifyEULA(): void {
		this.isNextDisabled = !(this.EULAForm!.isAccepted && this.EULAForm!.endScroll);
	}

	/**
	* Handle Privacy Policy document acceptance.
	*/
	acceptPrivacyPolicy(value: boolean): void {
		this.PPForm!.isAccepted = value;
		if (!this.PPForm!.isAccepted) {
			this.PPForm!.marketingValue = null;
			this.PPForm!.profilationValue = null;
		}
		this.verifyPrivacyPolicy();
	}

	/**
	* Handle Marketing acceptance.
	*/
	acceptMarketing(value: boolean): void {
		if (this.PPForm!.isAccepted) {
			this.PPForm!.marketingValue = value;
			this.verifyPrivacyPolicy();
		}
	}

	/**
	* Handle Profilation acceptance.
	*/
	acceptProfilation(value: boolean): void {
		if (this.PPForm!.isAccepted) {
			this.PPForm!.profilationValue = value;
			this.verifyPrivacyPolicy();
		}
	}

	/**
	* Verify if Provacy Policy document is accepted.
	*/
	verifyPrivacyPolicy(): void {
		if (this.PPForm!.isAccepted && this.PPForm!.endScroll) {
			if (this.PPForm!.marketingTxt && this.PPForm!.profilationTxt) {
				this.isNextDisabled = this.PPForm!.marketingValue == null || this.PPForm!.profilationValue == null;
			} else if (this.PPForm!.marketingTxt) {
				this.isNextDisabled = this.PPForm!.marketingValue == null;
			} else if (this.PPForm!.profilationTxt) {
				this.isNextDisabled = this.PPForm!.profilationValue == null;
			} else {
				this.isNextDisabled = !this.PPForm!.isAccepted;
			}
		} else {
			this.isNextDisabled = true;
		}
	}

	/**
	* Handle Data Process Agreement acceptance.
	*/
	acceptDataProcessAgreement(value: boolean): void {
		this.DPAForm!.isAccepted = value;
		this.verifyDPA();
	}

	/**
	* Verify if Data Process Agreement document is accepted.
	*/
	verifyDPA(): void {
		this.isNextDisabled = !(this.DPAForm!.isAccepted && this.DPAForm!.endScroll);
	}

	/**
	* Handle send request button.
	*/
	nextOrSend(): void {
		if ((!this.PPForm!.isAccepted && this.DPAForm === null) || (this.PPForm!.isAccepted && this.DPAForm != null)) {
			(this.mySwiper as Swiper).slideNext();
			this.nextOrSendButton = this.sendLbl;
		}
		if (this.EULAForm!.isAccepted && this.PPForm!.isAccepted && (this.DPAForm == null || this.DPAForm.isAccepted)) {
			this.confirm();
		} else {
			(this.mySwiper as Swiper).slideNext();
			this.isNextDisabled = true;
		}
	}

	private getLegalDocumentAcceptance(): LegalDocumentAcceptance[] {
		const legalDocAcceptance: LegalDocumentAcceptance[] = [];
		for (const doc of this._legalDocuments) {
			legalDocAcceptance.push({ id: doc.id!, type: doc.type, isAccepted: true });
		}
		let otherConsensus: NoMandatoryConsensus[] = [];
		if (this.PPForm!.marketingId) {
			otherConsensus.push({ documentItemID: this.PPForm!.marketingId, isAccepted: !!this.PPForm!.marketingValue });
		}
		if (this.PPForm!.profilationId) {
			otherConsensus.push({ documentItemID: this.PPForm!.profilationId, isAccepted: !!this.PPForm!.profilationValue });
		}
		const privacyPolicy = legalDocAcceptance.find(doc => doc.type === LegalDocumentTypeEnum.PrivacyPolicy);
		if (privacyPolicy) privacyPolicy.otherConsensus = otherConsensus.length ? otherConsensus : undefined;
		return legalDocAcceptance;
	}

	/**
	* Confirm legal documents.
	*/
	private confirm(): void {
		const legalDocAcceptance = this.getLegalDocumentAcceptance();
		this.service.handleLegalDocAcceptance(legalDocAcceptance, this.isAcceptance);
	}

	/**
	* Handle EULA box scroll event.
	*/
	onEULAScroll(event: any): void {
		if (!this.EULAForm!.endScroll) {
			if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
				this.EULAForm!.endScroll = true;
				this.verifyEULA();
			}
		}
	}

	/**
	* Handle Privacy Policy box scroll event.
	*/
	onPPScroll(event: any): void {
		if (!this.PPForm!.endScroll) {
			if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
				this.PPForm!.endScroll = true;
				this.verifyPrivacyPolicy();
			}
		}
	}

	/**
	* Handle Data Process Agreement box scroll event.
	*/
	onDPAScroll(event: any): void {
		if (!this.DPAForm!.endScroll) {
			if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
				this.DPAForm!.endScroll = true;
				this.verifyDPA();
			}
		}
	}

}

class EULAForm {
	text: string | null = null;
	effectiveDate: Date | null = null;
	endScroll: boolean | null = null;
	eula1: string | null = null;
	add_msg: string | null = null;
	isAccepted: boolean | null = null;
};

class PPForm {
	text: string | null = null;
	effectiveDate: Date | null = null;
	endScroll: boolean | null = null;
	ack: string | null = null;
	isAccepted: boolean | null = null;
	marketingId: number | null = null;
	marketingTxt: string | null = null;
	marketingValue: boolean | null = null;
	profilationId: number | null = null;
	profilationTxt: string | null = null;
	profilationValue: boolean | null = null;
};

class DPAForm {
	text: string | null = null;
	effectiveDate: Date | null = null;
	endScroll: boolean | null = null;
	ack: string | null = null;
	isAccepted: boolean | null = null;
};
